import { ApiError } from "@/services/BaseService";
import { Role } from "@/types/user";

export type MismatchedRole = {
  id: string;
  baseRole: Role | string;
};

export type StructuredCustomRoleError = {
  message: string;
  unknownPermissionKeys?: string[];
  violatingPermissionKeys?: string[];
  baseRole?: Role | string;
  mismatchedRoles?: MismatchedRole[];
  missingCustomRoleIds?: string[];
  userRole?: Role | string;
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isMismatchedRoleArray = (value: unknown): value is MismatchedRole[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      item !== null &&
      typeof item === "object" &&
      typeof (item as Record<string, unknown>).id === "string",
  );

/**
 * Pulls structured error fields out of an API failure (when the backend includes
 * machine-readable info such as `mismatchedRoles`, `unknownPermissionKeys`,
 * `violatingPermissionKeys`, or `missingCustomRoleIds`).
 */
export const extractCustomRoleError = (
  error: unknown,
): StructuredCustomRoleError => {
  const fallbackMessage =
    error instanceof Error ? error.message : "Unknown error";

  if (!(error instanceof ApiError) || !error.details) {
    return { message: fallbackMessage };
  }

  const details = error.details;
  const result: StructuredCustomRoleError = { message: error.message };

  if (isStringArray(details.unknownPermissionKeys)) {
    result.unknownPermissionKeys = details.unknownPermissionKeys;
  }
  if (isStringArray(details.violatingPermissionKeys)) {
    result.violatingPermissionKeys = details.violatingPermissionKeys;
  }
  if (typeof details.baseRole === "string") {
    result.baseRole = details.baseRole;
  }
  if (isMismatchedRoleArray(details.mismatchedRoles)) {
    result.mismatchedRoles = details.mismatchedRoles;
  }
  if (isStringArray(details.missingCustomRoleIds)) {
    result.missingCustomRoleIds = details.missingCustomRoleIds;
  }
  if (typeof details.userRole === "string") {
    result.userRole = details.userRole;
  }

  return result;
};

/**
 * Builds a human-readable, multi-line error message for toasts/banners.
 * Falls back to the plain `message` when no structured fields are present.
 */
export const formatCustomRoleError = (
  error: unknown,
  knownRoleNamesById?: Map<string, string>,
): string => {
  const parsed = extractCustomRoleError(error);
  const lines: string[] = [parsed.message];

  if (parsed.unknownPermissionKeys?.length) {
    lines.push(
      `Unknown permission keys: ${parsed.unknownPermissionKeys.join(", ")}`,
    );
  }
  if (parsed.violatingPermissionKeys?.length) {
    const ceilingHint = parsed.baseRole
      ? ` (ceiling for ${parsed.baseRole})`
      : "";
    lines.push(
      `Permissions outside the privilege ceiling${ceilingHint}: ${parsed.violatingPermissionKeys.join(
        ", ",
      )}`,
    );
  }
  if (parsed.mismatchedRoles?.length) {
    const labels = parsed.mismatchedRoles.map((entry) => {
      const name = knownRoleNamesById?.get(entry.id);
      return `${name ?? entry.id} (${entry.baseRole})`;
    });
    const userRole = parsed.userRole ? ` for ${parsed.userRole}` : "";
    lines.push(
      `Roles with non-matching baseRole${userRole}: ${labels.join(", ")}`,
    );
  }
  if (parsed.missingCustomRoleIds?.length) {
    lines.push(
      `Missing custom role IDs: ${parsed.missingCustomRoleIds.join(", ")}`,
    );
  }

  return lines.join("\n");
};
